import { type IconDefinition } from '@/components/ui/app-icon';
import {
  iconAddressBook,
  iconArrowRight,
  iconArrowUpRightFromSquare,
  iconBan,
  iconBook,
  iconBrain,
  iconBuildings,
  iconChartBar,
  iconCircleCheck,
  iconCoin,
  iconCopy,
  iconEnvelope,
  iconFileCsv,
  iconFilter,
  iconFolderPlus,
  iconGlobe,
  iconInbox,
  iconMagnifyingGlass,
  iconPen,
  iconPlug,
  iconReply,
  iconRobot,
  iconSparkles,
  iconTable,
  iconToggleOn,
  iconTrash,
  iconUserMagnifyingGlass,
  iconUserPlus,
  iconUsers,
  iconWavePulse,
  iconWebhook,
} from '@/lib/icons';

export interface ToolStep {
  done: string;
  running: string;
  icon: IconDefinition;
}

const step = (
  done: string,
  running: string,
  icon: IconDefinition
): ToolStep => ({ done, running, icon });

/** Clu's Instantly cards by the action each confirms. A card is built under
 *  its action's name, so the running words stay neutral. */
const INSTANTLY_ACTION_STEPS: Record<string, ToolStep> = {
  instantly_create_campaign: step(
    'Created the campaign in Instantly',
    'Preparing the Instantly campaign',
    iconEnvelope
  ),
  instantly_edit_campaign: step(
    'Edited the campaign in Instantly',
    'Preparing the campaign edit',
    iconPen
  ),
  instantly_campaign_status: step(
    'Changed the campaign in Instantly',
    'Preparing the campaign change',
    iconToggleOn
  ),
  instantly_add_leads: step(
    'Added leads in Instantly',
    'Preparing the leads for Instantly',
    iconUserPlus
  ),
  instantly_change_leads: step(
    'Changed leads in Instantly',
    'Preparing the lead change',
    iconUsers
  ),
  instantly_warmup: step(
    'Changed warmup in Instantly',
    'Preparing the warmup change',
    iconWavePulse
  ),
  instantly_blocklist_add: step(
    'Added to the Instantly blocklist',
    'Preparing the blocklist entries',
    iconBan
  ),
  instantly_resume_webhook: step(
    'Resumed the Instantly webhook',
    'Preparing the webhook',
    iconWebhook
  ),
};

/** The actions Clu's Instantly cards confirm (cleanse-api
 *  toolkits/instantly.py INSTANTLY_ACTIONS). */
export const INSTANTLY_ACTIONS: ReadonlySet<string> = new Set(
  Object.keys(INSTANTLY_ACTION_STEPS)
);

/** Removing Instantly leads deletes them for good, so its card always reads
 *  as a delete. Moving them or setting their interest never does. */
export function isInstantlyRemove(request: {
  tool_name?: string;
  arguments?: Record<string, unknown>;
}): boolean {
  return (
    request.tool_name === 'instantly_change_leads' &&
    request.arguments?.action === 'remove'
  );
}

/** Clu's Instantly reads, each card (request_<action>) and each confirmed
 *  action. */
const INSTANTLY_TOOL_STEPS: Record<string, ToolStep> = {
  list_instantly_campaigns: step(
    'Read your Instantly campaigns',
    'Reading your Instantly campaigns',
    iconEnvelope
  ),
  get_instantly_campaign: step(
    'Read an Instantly campaign',
    'Reading an Instantly campaign',
    iconEnvelope
  ),
  find_instantly_leads: step(
    'Looked up leads in Instantly',
    'Looking up leads in Instantly',
    iconUserMagnifyingGlass
  ),
  list_instantly_inboxes: step(
    'Read your Instantly inboxes',
    'Reading your Instantly inboxes',
    iconInbox
  ),
  get_instantly_inbox_health: step(
    'Checked inbox health in Instantly',
    'Checking inbox health in Instantly',
    iconInbox
  ),
  list_instantly_replies: step(
    'Read Instantly replies',
    'Reading Instantly replies',
    iconReply
  ),
  get_instantly_analytics: step(
    'Read Instantly analytics',
    'Reading Instantly analytics',
    iconChartBar
  ),
  list_instantly_blocklist: step(
    'Read the Instantly blocklist',
    'Reading the Instantly blocklist',
    iconBan
  ),
  get_instantly_webhook_status: step(
    'Checked Instantly webhooks',
    'Checking Instantly webhooks',
    iconWebhook
  ),
  ...Object.fromEntries(
    Object.entries(INSTANTLY_ACTION_STEPS).map(([action, { icon }]) => [
      `request_${action}`,
      step('Asked to confirm', 'Asking to confirm', icon),
    ])
  ),
  ...INSTANTLY_ACTION_STEPS,
};

/**
 * Clu's own tools as steps, the same kind of labels an agent's tools read as
 * (see AGENT_TOOL_STEPS): what the step did once done, what it does while it
 * runs, and its icon. Search, lists, credits, CRM reads, memory and each
 * confirmed action. The names are the ones the stream carries, so a
 * confirmed action reads under its action (import_to_list, not
 * request_import_to_list).
 */
export const CLU_TOOL_STEPS: Record<string, ToolStep> = {
  people_search: step(
    'Searched people',
    'Searching people',
    iconMagnifyingGlass
  ),
  show_more_results: step(
    'Loaded more people',
    'Loading more people',
    iconMagnifyingGlass
  ),
  company_search: step(
    'Searched companies',
    'Searching companies',
    iconBuildings
  ),
  web_people_search: step('Searched the web', 'Searching the web', iconGlobe),
  get_lead_lists: step('Read your lists', 'Reading your lists', iconTable),
  get_leads: step('Read the leads', 'Reading the leads', iconUsers),
  create_lead_list: step('Created a list', 'Creating a list', iconFolderPlus),
  rename_lead_list: step('Renamed the list', 'Renaming the list', iconPen),
  copy_leads: step('Copied leads', 'Copying leads', iconCopy),
  move_leads: step('Moved leads', 'Moving leads', iconArrowRight),
  delete_leads: step('Deleted leads', 'Deleting leads', iconTrash),
  delete_lead_list: step('Deleted the list', 'Deleting the list', iconTrash),
  // Google Sheets + Docs (CLE-515).
  export_list_to_google_sheet: step(
    'Exported to Google Sheets',
    'Exporting to Google Sheets',
    iconTable
  ),
  import_pasted_rows: step(
    'Read the pasted rows',
    'Reading the pasted rows',
    iconFileCsv
  ),
  open_google_sheet: step(
    'Opened a Google Sheet',
    'Opening a Google Sheet',
    iconTable
  ),
  create_google_doc: step(
    'Created a Google Doc',
    'Creating a Google Doc',
    iconArrowUpRightFromSquare
  ),
  // Airtable (CLE-516).
  list_airtable_bases: step(
    'Checked your Airtable bases',
    'Checking your Airtable bases',
    iconTable
  ),
  export_list_to_airtable: step(
    'Started an Airtable export',
    'Starting an Airtable export',
    iconTable
  ),
  filter_list: step('Applied filters', 'Applying filters', iconFilter),
  apply_filters: step('Applied filters', 'Applying filters', iconFilter),
  navigate_to: step(
    'Opened a page',
    'Opening a page',
    iconArrowUpRightFromSquare
  ),
  get_credit_balance: step('Checked credits', 'Checking credits', iconCoin),
  estimate_cost: step('Estimated the cost', 'Estimating the cost', iconCoin),
  list_saved_agents: step('Read your agents', 'Reading your agents', iconRobot),
  match_crm_people: step(
    'Matched people in your CRM',
    'Matching people in your CRM',
    iconAddressBook
  ),
  read_crm_account: step(
    'Read the CRM account',
    'Reading the CRM account',
    iconAddressBook
  ),
  search_crm_records: step(
    'Searched your CRM',
    'Searching your CRM',
    iconAddressBook
  ),
  read_crm_record: step(
    'Read a CRM record',
    'Reading a CRM record',
    iconAddressBook
  ),
  // A read of any connected tool's API. Its args name the tool, so
  // stepLabel says which one (or "Checking connections" with none).
  read_connection_api: step(
    'Read a connected tool',
    'Reading a connected tool',
    iconPlug
  ),
  // A change to a connected tool's record: its card, then the confirmed
  // write under its action's name.
  request_connection_api_write: step(
    'Asked to confirm',
    'Asking to confirm',
    iconPlug
  ),
  connection_api_write: step(
    'Changed a connected tool',
    'Changing a connected tool',
    iconPlug
  ),
  read_clu_memory: step(
    'Read saved context',
    'Reading saved context',
    iconBrain
  ),
  // The confirmed save or forget, under its action's name.
  change_clu_memory: step(
    'Updated saved context',
    'Updating saved context',
    iconBrain
  ),
  // What Clu learns (CLE-562). It may save, send for review or refuse, so the
  // step claims none of them; the row under the reply says which.
  propose_learning: step(
    'Checked what to remember',
    'Checking what to remember',
    iconBrain
  ),
  read_skill: step('Read a skill', 'Reading a skill', iconBook),
  import_to_list: step('Imported leads', 'Importing leads', iconUsers),
  enrich_leads: step('Started enrichment', 'Starting enrichment', iconSparkles),
  create_smart_column: step(
    'Started a smart column',
    'Starting a smart column',
    iconSparkles
  ),
  validate_contacts: step(
    'Validated contacts',
    'Validating contacts',
    iconCircleCheck
  ),
  reverse_email_lookup: step(
    'Looked up an email',
    'Looking up an email',
    iconEnvelope
  ),
  prospect_person: step(
    'Looked up a person',
    'Looking up a person',
    iconUserMagnifyingGlass
  ),
  ...INSTANTLY_TOOL_STEPS,
};
